/**
 * EntityDisambiguationCard — Refine-agent interrupt UI for ambiguous entities.
 *
 * Shown in the left panel when the backend returns needs_clarification.
 * Users pick one close match per term (or search the portfolio via `/`), then
 * Continue with selected_entities, or Skip with an empty selection.
 *
 * A term can also be Ignored — the suggested name isn't a real entity at all, so it needs no
 * pick and is reported back separately (`ignoredTerms`) rather than resolved.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import type { LookupCategory, LookupDimensionGroup, LookupItem } from '../AskAsperAgentic.types';
import { useSlashLookup, replaceSlashTokenQuery } from '../SearchBar/useSlashLookup';
import LookupCategoryDropdown from '../SearchBar/LookupCategoryDropdown';
import LookupSuggestionDropdown from '../SearchBar/LookupSuggestionDropdown';

export interface EntityDisambiguationMatch {
    column_name: string;
    entity_name: string;
}

export interface EntityDisambiguationTerm {
    entity_name: string;
    close_matches: EntityDisambiguationMatch[];
    /**
     * True for an entity the backend already matched in the same question (`selected_entities`).
     * Rendered collapsed and pre-selected; it does not gate Continue.
     */
    confirmed?: boolean;
}

export interface EntityDisambiguationCardProps {
    terms: EntityDisambiguationTerm[];
    /** Initial selections (e.g. from history). Keyed by ambiguous entity_name. */
    initialSelections?: Record<string, EntityDisambiguationMatch>;
    /** entity_names previously marked as Ignore (e.g. from history), so a resolved card re-renders. */
    initialIgnored?: string[];
    lookupItems?: LookupItem[];
    lookupCategories?: LookupCategory[];
    lookupDimensionGroups?: LookupDimensionGroup[];
    /** When true, selections + actions are locked (after Continue/Skip or history). */
    resolved?: boolean;
    /**
     * `selected` is the full set to resume with (confirmed entities first, then the user's picks).
     * `selectionsByTerm` maps each ambiguous term to the match chosen for it, so the host can
     * persist the mapping and re-render a resolved card correctly.
     * `ignoredTerms` are the entity_names the user dismissed; they appear in neither of the above.
     */
    onContinue?: (
        selected: EntityDisambiguationMatch[],
        selectionsByTerm: Record<string, EntityDisambiguationMatch>,
        ignoredTerms: string[],
    ) => void;
    onSkip?: () => void;
    onLoadLookups?: () => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

function formatColumnLabel(columnName: string): string {
    return columnName
        .trim()
        .replace(/_/g, '-')
        .replace(/\s+/g, '-')
        .toLowerCase();
}

function matchKey(match: EntityDisambiguationMatch): string {
    return `${match.column_name}::${match.entity_name}`;
}

interface TermPortfolioSearchProps {
    termEntityName: string;
    lookupItems?: LookupItem[];
    lookupCategories?: LookupCategory[];
    lookupDimensionGroups?: LookupDimensionGroup[];
    disabled?: boolean;
    onPick: (item: LookupItem) => void;
    onClose?: () => void;
    onLoadLookups?: () => void;
    'data-test-id'?: string;
}

const TermPortfolioSearch: React.FC<TermPortfolioSearchProps> = ({
    termEntityName,
    lookupItems,
    lookupCategories,
    lookupDimensionGroups,
    disabled,
    onPick,
    onClose,
    onLoadLookups,
    'data-test-id': dataTestId,
}) => {
    const [value, setValue] = useState('/');
    const [caretPos, setCaretPos] = useState(1);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const pendingCaretRef = useRef<number | null>(null);
    const hasLoadedRef = useRef(false);

    const slash = useSlashLookup(value, caretPos, lookupItems, {
        categories: lookupCategories,
        dimensionGroups: lookupDimensionGroups,
    });

    useEffect(() => {
        inputRef.current?.focus();
        inputRef.current?.setSelectionRange(1, 1);
    }, []);

    useEffect(() => {
        if (pendingCaretRef.current == null || !inputRef.current) return;
        const pos = pendingCaretRef.current;
        pendingCaretRef.current = null;
        inputRef.current.setSelectionRange(pos, pos);
        setCaretPos(pos);
    }, []);

    const syncCaret = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        setCaretPos(el.selectionStart ?? 0);
    }, []);

    const acceptSuggestion = useCallback(
        (index: number) => {
            // In category mode the rows clicked are `matchingValues`; `suggestions` is only
            // populated once a category has been picked (mode === 'search').
            const match = slash.mode === 'search' ? slash.suggestions[index] : slash.matchingValues[index];
            if (!match) return;
            const result = slash.accept(match.item);
            if (!result) return;
            onPick(result.item);
        },
        [slash, onPick],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value;
            // If user backspaced away the slash, close the portfolio search
            if (next === '' || next === '/') {
                if (next === '') {
                    onClose?.();
                    return;
                }
            }
            // Keep a leading slash so the slash-lookup token stays active.
            const normalized = next.startsWith('/') ? next : `/${next.replace(/^\/*/, '')}`;
            setValue(normalized);
            const nextCaret = e.target.selectionStart ?? normalized.length;
            setCaretPos(Math.min(nextCaret, normalized.length));
        },
        [onClose],
    );

    const handleFocus = useCallback(() => {
        if (!hasLoadedRef.current) {
            hasLoadedRef.current = true;
            onLoadLookups?.();
        }
    }, [onLoadLookups]);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!slash.isActive) return;
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (containerRef.current && (target === containerRef.current || containerRef.current.contains(target))) {
                return;
            }
            slash.close();
            onClose?.();
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [slash.isActive, slash.close, onClose]);

    const updateSlashQuery = useCallback(
        (query: string) => {
            const result = replaceSlashTokenQuery(value, slash.range, query);
            if (!result) return;
            const { nextValue, nextCaret } = result;
            setValue(nextValue);
            pendingCaretRef.current = nextCaret;
            setCaretPos(nextCaret);
        },
        [slash.range, value],
    );

    const handleLookupSearchKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex + 1);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex - 1);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (slash.suggestions.length > 0) {
                    acceptSuggestion(slash.highlightIndex);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                slash.goBack();
            }
        },
        [slash, acceptSuggestion],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!slash.isActive) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex + 1);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                slash.setHighlightIndex(slash.highlightIndex - 1);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const result = slash.activateHighlight();
                if (result?.kind === 'value') {
                    onPick(result.item);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                if (slash.mode === 'search') {
                    slash.goBack();
                } else {
                    slash.close();
                    onClose?.();
                }
            }
        },
        [slash, onPick, onClose],
    );

    return (
        <div ref={containerRef} className="relative mt-2" data-test-id={dataTestId}>
            {slash.isActive && slash.mode === 'category' ? (
                <LookupCategoryDropdown
                    scoredCategories={slash.scoredCategories}
                    matchingValues={slash.matchingValues}
                    highlightIndex={slash.highlightIndex}
                    onHighlight={slash.setHighlightIndex}
                    onSelectCategory={(id) => slash.pickCategory(id)}
                    onSelectValue={acceptSuggestion}
                    query={slash.query}
                    hasNoMatches={slash.hasNoMatches}
                    contextLabel={slash.activeDimensionGroupLabel}
                    onBack={
                        slash.activeDimensionGroupId != null ? slash.goBack : undefined
                    }
                    className="!absolute !bottom-full !top-auto !mb-2 !mt-0 left-0 right-0 !z-50"
                    data-test-id={dataTestId ? `${dataTestId}-category` : undefined}
                />
            ) : (
                <LookupSuggestionDropdown
                    suggestions={slash.isActive ? slash.suggestions : []}
                    highlightIndex={slash.highlightIndex}
                    onHighlight={slash.setHighlightIndex}
                    onSelect={acceptSuggestion}
                    categoryLabel={slash.activeCategoryLabel}
                    query={slash.query}
                    onQueryChange={updateSlashQuery}
                    onSearchKeyDown={handleLookupSearchKeyDown}
                    onBack={slash.goBack}
                    className="!absolute !bottom-full !top-auto !mb-2 !mt-0 left-0 right-0 !z-50"
                    data-test-id={dataTestId ? `${dataTestId}-suggestions` : undefined}
                />
            )}
            <input
                ref={inputRef}
                type="text"
                value={value}
                disabled={disabled}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onKeyUp={syncCaret}
                onClick={syncCaret}
                onSelect={syncCaret}
                onFocus={handleFocus}
                aria-label={`Search portfolio for ${termEntityName}`}
                className="w-full rounded-lg border border-[#e8eafa] bg-white px-3 py-2 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.88)] outline-none focus:border-[#3B2C77] focus:shadow-[0_0_0_2px_rgba(59,44,119,0.2)]"
                data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
            />
            <p className="mt-1.5 font-['Open_Sans'] text-[12px] leading-4 text-[#A1A1AA]">
                Pick an entity type, or select a value directly.
            </p>
        </div>
    );
};

const EntityDisambiguationCard: React.FC<EntityDisambiguationCardProps> = ({
    terms,
    initialSelections,
    initialIgnored,
    lookupItems,
    lookupCategories,
    lookupDimensionGroups,
    resolved = false,
    onContinue,
    onSkip,
    onLoadLookups,
    'data-test': dataTest,
    'data-test-id': dataTestId = 'entity-disambiguation-card',
}) => {
    const theme = useTheme();
    const [selections, setSelections] = useState<Record<string, EntityDisambiguationMatch>>(
        () => initialSelections ?? {},
    );
    /** Portfolio picks (and history selections) that aren't in the original close_matches. */
    const [extraMatches, setExtraMatches] = useState<Record<string, EntityDisambiguationMatch[]>>(
        () => {
            const seed: Record<string, EntityDisambiguationMatch[]> = {};
            if (!initialSelections) return seed;
            for (const term of terms) {
                const picked = initialSelections[term.entity_name];
                if (!picked) continue;
                const alreadyListed = (term.close_matches ?? []).some(
                    (m) => matchKey(m) === matchKey(picked),
                );
                if (!alreadyListed) seed[term.entity_name] = [picked];
            }
            return seed;
        },
    );
    const [portfolioOpenFor, setPortfolioOpenFor] = useState<string | null>(null);
    /** Terms the user dismissed as not-a-real-entity; they need no pick and never resume. */
    const [ignoredTerms, setIgnoredTerms] = useState<Set<string>>(
        () => new Set(initialIgnored ?? []),
    );
    // Confirmed rows (backend already matched them) start collapsed — only the terms that still
    // need a pick are open. Terms already ignored (history) stay collapsed too.
    const [expandedTerms, setExpandedTerms] = useState<Set<string>>(() => {
        const alreadyIgnored = new Set(initialIgnored ?? []);
        return new Set(
            terms
                .filter((t) => !t.confirmed && !alreadyIgnored.has(t.entity_name))
                .map((t) => t.entity_name),
        );
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialSelections) {
            setSelections(initialSelections);
            setExtraMatches((prev) => {
                const next = { ...prev };
                for (const term of terms) {
                    const picked = initialSelections[term.entity_name];
                    if (!picked) continue;
                    const listed = [
                        ...(term.close_matches ?? []),
                        ...(next[term.entity_name] ?? []),
                    ];
                    if (listed.some((m) => matchKey(m) === matchKey(picked))) continue;
                    next[term.entity_name] = [...(next[term.entity_name] ?? []), picked];
                }
                return next;
            });
        }
    }, [initialSelections, terms]);

    // Keyed on contents, not array identity: the host rebuilds `initialIgnored` on every render,
    // so setting state unconditionally would loop.
    const initialIgnoredKey = (initialIgnored ?? []).join(' ');
    useEffect(() => {
        if (!initialIgnored) return;
        setIgnoredTerms((prev) => {
            const next = new Set(initialIgnored);
            if (prev.size === next.size && [...next].every((n) => prev.has(n))) return prev;
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialIgnoredKey]);

    useEffect(() => {
        if (resolved) setIsSubmitting(false);
    }, [resolved]);

    // Only the ambiguous terms are being asked about — confirmed rows arrive pre-selected and
    // must not be counted in the header or gate Continue.
    const ambiguousTerms = useMemo(() => terms.filter((t) => !t.confirmed), [terms]);
    const termCount = ambiguousTerms.length;
    /** Entities that will actually resume: every non-ignored term that has a selection. */
    const resumingSelected = useMemo(
        () =>
            terms
                .filter((t) => !ignoredTerms.has(t.entity_name))
                .map((t) => selections[t.entity_name])
                .filter((m): m is EntityDisambiguationMatch => m != null),
        [terms, ignoredTerms, selections],
    );
    // Ignored terms need no pick. Continue still requires something left to proceed with —
    // an all-ignored card has no entities to resume on, so Skip is the exit there.
    const allSelected = useMemo(
        () =>
            ambiguousTerms.length > 0 &&
            ambiguousTerms.every(
                (t) => ignoredTerms.has(t.entity_name) || selections[t.entity_name] != null,
            ),
        [ambiguousTerms, ignoredTerms, selections],
    );
    const canContinue = !resolved && allSelected && resumingSelected.length > 0;

    const matchesForTerm = useCallback(
        (term: EntityDisambiguationTerm): EntityDisambiguationMatch[] => {
            const base = term.close_matches ?? [];
            const extras = extraMatches[term.entity_name] ?? [];
            if (extras.length === 0) return base;
            const seen = new Set(base.map(matchKey));
            const merged = [...base];
            for (const extra of extras) {
                const key = matchKey(extra);
                if (seen.has(key)) continue;
                seen.add(key);
                merged.push(extra);
            }
            return merged;
        },
        [extraMatches],
    );

    const selectMatch = useCallback(
        (termName: string, match: EntityDisambiguationMatch) => {
            if (resolved) return;
            setSelections((prev) => ({ ...prev, [termName]: match }));
            setPortfolioOpenFor((open) => (open === termName ? null : open));
            setExpandedTerms((prev) => {
                const next = new Set(prev);
                next.delete(termName);
                return next;
            });
        },
        [resolved],
    );

    const toggleTerm = useCallback(
        (termName: string) => {
            // An ignored row has nothing to choose from — keep it collapsed until it's un-ignored.
            if (ignoredTerms.has(termName)) return;
            setExpandedTerms((prev) => {
                const next = new Set(prev);
                if (next.has(termName)) {
                    next.delete(termName);
                } else {
                    next.add(termName);
                }
                return next;
            });
        },
        [ignoredTerms],
    );

    /**
     * Ignore drops the term from the resume entirely: its pick is cleared, its row collapses and
     * the portfolio search closes. Un-ignoring re-opens it so a pick can be made.
     */
    const toggleIgnore = useCallback(
        (termName: string) => {
            if (resolved) return;
            const wasIgnored = ignoredTerms.has(termName);
            setIgnoredTerms((prev) => {
                const next = new Set(prev);
                if (wasIgnored) next.delete(termName);
                else next.add(termName);
                return next;
            });

            if (wasIgnored) {
                setExpandedTerms((exp) => new Set(exp).add(termName));
                return;
            }
            setSelections((sel) => {
                if (sel[termName] == null) return sel;
                const rest = { ...sel };
                delete rest[termName];
                return rest;
            });
            setExpandedTerms((exp) => {
                const nextExp = new Set(exp);
                nextExp.delete(termName);
                return nextExp;
            });
            setPortfolioOpenFor((open) => (open === termName ? null : open));
        },
        [resolved, ignoredTerms],
    );

    const handlePortfolioPick = useCallback(
        (termName: string, item: LookupItem) => {
            const match: EntityDisambiguationMatch = {
                column_name: item.column_name,
                entity_name: item.entity_name,
            };
            // Surface the portfolio choice in the option list so the user can
            // see what we're continuing with (selected state).
            setExtraMatches((prev) => {
                const existing = prev[termName] ?? [];
                const term = terms.find((t) => t.entity_name === termName);
                const alreadyListed = [
                    ...(term?.close_matches ?? []),
                    ...existing,
                ].some((m) => matchKey(m) === matchKey(match));
                if (alreadyListed) return prev;
                return { ...prev, [termName]: [...existing, match] };
            });
            selectMatch(termName, match);
            setPortfolioOpenFor(null);
        },
        [selectMatch, terms],
    );

    const handleContinue = useCallback(() => {
        if (!canContinue || isSubmitting) return;
        setIsSubmitting(true);
        // Confirmed entities come first (terms order), then the picks for the ambiguous terms.
        // Ignored terms are in neither list — they're reported separately.
        const selected = resumingSelected;
        const selectionsByTerm: Record<string, EntityDisambiguationMatch> = {};
        for (const term of ambiguousTerms) {
            if (ignoredTerms.has(term.entity_name)) continue;
            const pick = selections[term.entity_name];
            if (pick) selectionsByTerm[term.entity_name] = pick;
        }
        // Report in card order so the prompt reads in the order the user saw the rows.
        const ignored = terms
            .map((t) => t.entity_name)
            .filter((name) => ignoredTerms.has(name));
        onContinue?.(selected, selectionsByTerm, ignored);
    }, [
        canContinue,
        isSubmitting,
        terms,
        ambiguousTerms,
        selections,
        ignoredTerms,
        resumingSelected,
        onContinue,
    ]);

    const handleSkip = useCallback(() => {
        if (resolved) return;
        onSkip?.();
    }, [resolved, onSkip]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-white rounded-xl border border-[#e8eafa]"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-entity-disambiguation-card
            >
                <div className="px-5 pt-5 pb-4 border-b border-[#e8eafa]">
                    <h3 className="m-0 font-['Open_Sans'] font-semibold text-[16px] leading-6 text-[rgba(0,0,0,0.88)]">
                        We couldn&apos;t find exact matches for {termCount} term
                        {termCount === 1 ? '' : 's'}
                    </h3>
                    <p className="mt-1 mb-0 font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.65)]">
                        Select the best match for each term to continue
                    </p>
                </div>

                <div className="px-5 py-4 space-y-3">
                    {terms.map((term) => {
                        const selected = selections[term.entity_name];
                        const portfolioOpen = portfolioOpenFor === term.entity_name;
                        const matches = matchesForTerm(term);
                        const isIgnored = ignoredTerms.has(term.entity_name);
                        const isExpanded = expandedTerms.has(term.entity_name) && !isIgnored;

                        return (
                            <div
                                key={term.entity_name}
                                data-test-id={`${dataTestId}-term-${term.entity_name}`}
                                data-ignored={isIgnored ? 'true' : undefined}
                                className={`border border-[#e8eafa] rounded-lg ${
                                    isIgnored ? 'opacity-60' : ''
                                }`}
                            >
                                {/* Header is a div, not a button: the Ignore control nests inside it
                                    and a button-in-button is invalid markup. */}
                                <div
                                    className={`w-full px-3 py-3 flex items-start justify-between gap-3 transition-colors ${
                                        isExpanded
                                            ? 'bg-[#f5f3ff]'
                                            : 'bg-white hover:bg-[#fafafa]'
                                    }`}
                                    data-test-id={`${dataTestId}-term-header-${term.entity_name}`}
                                >
                                    <div
                                        role="button"
                                        tabIndex={resolved || isIgnored ? -1 : 0}
                                        aria-expanded={isExpanded}
                                        aria-disabled={resolved || isIgnored}
                                        onClick={() => {
                                            if (resolved) return;
                                            toggleTerm(term.entity_name);
                                        }}
                                        onKeyDown={(e) => {
                                            if (resolved || isIgnored) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleTerm(term.entity_name);
                                            }
                                        }}
                                        className={`flex flex-wrap items-center gap-x-2 gap-y-1 flex-1 text-left min-w-0 ${
                                            resolved || isIgnored ? 'cursor-default' : 'cursor-pointer'
                                        }`}
                                    >
                                        <p className="m-0 font-['Open_Sans'] text-[12px] leading-4 tracking-[0.04em] text-[rgba(0,0,0,0.45)] uppercase shrink-0">
                                            {isIgnored
                                                ? 'Ignored'
                                                : term.confirmed
                                                  ? 'Matched'
                                                  : 'Match for'}{' '}
                                        </p>
                                        <code
                                            className={`font-mono normal-case tracking-normal text-[#3b2c77] bg-[#f1edff] px-1.5 py-0.5 rounded text-[12px] break-words whitespace-normal ${
                                                isIgnored ? 'line-through' : ''
                                            }`}
                                        >
                                            {term.entity_name}
                                        </code>
                                        {selected && !term.confirmed && !isIgnored && (
                                            <span className="font-['Open_Sans'] text-[12px] leading-4 text-[#3b2c77] break-words whitespace-normal">
                                                — {selected.entity_name}
                                            </span>
                                        )}
                                        {term.confirmed && !isIgnored && (
                                            <span className="font-['Open_Sans'] text-[12px] leading-4 text-[#3b2c77] bg-[#f1edff] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap">
                                                from your question
                                            </span>
                                        )}
                                    </div>
                                    {!resolved && (
                                        <button
                                            type="button"
                                            onClick={() => toggleIgnore(term.entity_name)}
                                            aria-label={`${isIgnored ? 'Undo ignore for' : 'Ignore'} ${term.entity_name}`}
                                            className="font-['Open_Sans'] text-[12px] leading-4 text-[rgba(0,0,0,0.45)] hover:text-[#3b2c77] bg-transparent border-0 p-0 mt-0.5 shrink-0 cursor-pointer whitespace-nowrap"
                                            data-test-id={`${dataTestId}-ignore-${term.entity_name}`}
                                        >
                                            {isIgnored ? 'Undo' : 'Ignore'}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        disabled={resolved || isIgnored}
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        onClick={() => toggleTerm(term.entity_name)}
                                        className={`bg-transparent border-0 p-0 shrink-0 ${
                                            resolved || isIgnored ? 'cursor-default' : 'cursor-pointer'
                                        }`}
                                    >
                                        <svg
                                            className={`w-5 h-5 text-[rgba(0,0,0,0.45)] shrink-0 transition-transform duration-200 mt-0.5 ${
                                                isExpanded ? 'rotate-180' : ''
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 9l6 6 6-6"
                                            />
                                        </svg>
                                    </button>
                                </div>

                                {isExpanded && (
                                    <div className="border-t border-[#e8eafa] px-3 py-3 space-y-2 bg-white">
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                            <span className="font-['Open_Sans'] text-[12px] leading-4 text-[rgba(0,0,0,0.45)] shrink-0">
                                                {matches.length === 0
                                                    ? 'No close matches found'
                                                    : `Showing top ${matches.length} match${
                                                          matches.length === 1 ? '' : 'es'
                                                      }`}
                                            </span>
                                        </div>

                                        {matches.map((match) => {
                                            const isSelected =
                                                selected != null && matchKey(selected) === matchKey(match);
                                            return (
                                                <button
                                                    key={matchKey(match)}
                                                    type="button"
                                                    disabled={resolved}
                                                    onClick={() => selectMatch(term.entity_name, match)}
                                                    className={`w-full flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${
                                                        isSelected
                                                            ? 'border-[#3B2C77] bg-[#f5f3ff]'
                                                            : 'border-[#e8eafa] bg-white hover:bg-[#fafafa]'
                                                    } ${resolved ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
                                                    data-test-id={`${dataTestId}-match-${term.entity_name}-${match.entity_name}`}
                                                >
                                                    <span className="font-['Open_Sans'] text-[14px] leading-5 text-[rgba(0,0,0,0.88)] break-words whitespace-normal min-w-0">
                                                        {match.entity_name}
                                                    </span>
                                                    <span className="font-['Open_Sans'] text-[12px] leading-4 text-[#3b2c77] bg-[#f1edff] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap">
                                                        {formatColumnLabel(match.column_name)}
                                                    </span>
                                                </button>
                                            );
                                        })}

                                        {!resolved && (
                                            <div className="mt-3 pt-2 border-t border-[#e8eafa]">
                                                {portfolioOpen ? (
                                                    <TermPortfolioSearch
                                                        termEntityName={term.entity_name}
                                                        lookupItems={lookupItems}
                                                        lookupCategories={lookupCategories}
                                                        lookupDimensionGroups={lookupDimensionGroups}
                                                        onPick={(item) =>
                                                            handlePortfolioPick(term.entity_name, item)
                                                        }
                                                        onClose={() => setPortfolioOpenFor(null)}
                                                        onLoadLookups={onLoadLookups}
                                                        data-test-id={`${dataTestId}-portfolio-${term.entity_name}`}
                                                    />
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setPortfolioOpenFor(term.entity_name);
                                                            onLoadLookups?.();
                                                        }}
                                                        className="font-['Open_Sans'] text-[14px] leading-5 text-[#3b2c77] hover:text-[#2a1f54] bg-transparent border-0 p-0 cursor-pointer"
                                                        data-test-id={`${dataTestId}-search-portfolio-${term.entity_name}`}
                                                    >
                                                        {matches.length === 0
                                                            ? 'Search portfolio'
                                                            : 'None of these — search portfolio'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-[#e8eafa]">
                    <button
                        type="button"
                        disabled={resolved}
                        onClick={handleSkip}
                        className={`font-['Open_Sans'] text-[14px] leading-5 bg-transparent border-0 p-0 ${
                            resolved
                                ? 'text-[rgba(0,0,0,0.25)] cursor-default'
                                : 'text-[rgba(0,0,0,0.65)] hover:text-[rgba(0,0,0,0.88)] cursor-pointer'
                        }`}
                        data-test-id={`${dataTestId}-skip`}
                    >
                        Skip
                    </button>
                    <button
                        type="button"
                        disabled={!canContinue || isSubmitting}
                        onClick={handleContinue}
                        className={`font-['Open_Sans'] text-[14px] leading-5 font-normal rounded px-3 py-1 transition-colors ${
                            canContinue && !isSubmitting
                                ? 'bg-[#3B2C77] text-[#fbfaff] hover:bg-gray-800 cursor-pointer'
                                : 'bg-black/50 text-[#fbfaff] cursor-not-allowed'
                        }`}
                        data-test-id={`${dataTestId}-continue`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default EntityDisambiguationCard;
